/**
 * Platform admin UI tokens — aligned with tickit-platform HTML mockups (Syne + Nunito).
 * Color values resolve via CSS custom properties (see platform.css) for dark-mode support.
 */
import { marketingFonts } from "./tickitMarketingTheme";
import { useThemeMode } from "./theme/ThemeModeProvider";
import { getPlatformColors } from "./theme/tickitThemes";

export const platformFonts = {
  display: marketingFonts.display,
  body: "'Nunito', 'Plus Jakarta Sans', 'TickitTwo', Helvetica, Arial, sans-serif",
};

/** @deprecated Prefer usePlatformColors() or CSS var(--platform-*) in sx */
export const platformColors = getPlatformColors("light");

export function usePlatformColors() {
  const { platformColors: colors } = useThemeMode();
  return colors;
}

export const platformRadius = {
  sm: "10px",
  md: "12px",
  lg: "18px",
  xl: "24px",
};

export const platformShadow = {
  card: "var(--platform-shadow-card)",
  cardHover: "var(--platform-shadow-card-hover)",
};

/** Matches HTML `.top-nav` height (60px) */
export const platformNavHeightPx = 60;

export const platformTableContainerSx = {
  overflowX: "auto",
  WebkitOverflowScrolling: "touch",
};

export const platformAppBarSx = {
  background: "rgba(14, 5, 32, 0.9) !important",
  backdropFilter: "blur(16px)",
  boxShadow: "none !important",
  borderBottom: "1px solid rgba(255, 255, 255, 0.08) !important",
  color: "#fff",
};

export const platformNavTabSx = {
  fontFamily: platformFonts.body,
  fontSize: "12.5px",
  fontWeight: 600,
  color: "rgba(255, 255, 255, 0.45)",
  textTransform: "none",
  borderRadius: "8px",
  px: 1.75,
  py: 0.75,
  minWidth: 0,
  "&:hover": {
    color: "rgba(255, 255, 255, 0.8)",
    bgcolor: "rgba(255, 255, 255, 0.06)",
  },
};

export const platformNavTabActiveSx = {
  ...platformNavTabSx,
  color: "#fff",
  bgcolor: "rgba(107, 45, 197, 0.4)",
};

export const platformLogoTextSx = {
  fontFamily: platformFonts.display,
  fontSize: 21,
  fontWeight: 800,
  color: "#fff",
  letterSpacing: "-0.5px",
  cursor: "pointer",
  "& span": { color: "var(--platform-brand-light)" },
};

/** Content area inside shell (HTML `.content`) */
export const platformContentAreaSx = {
  flex: 1,
  minWidth: 0,
  overflow: "auto",
  backgroundColor: "var(--platform-content-bg)",
  px: { xs: 2, sm: 3.75 },
  py: { xs: 2.5, sm: 3.25 },
};

/** Page wrapper — padding lives on shell content area */
export const platformPageSx = {
  minHeight: "100%",
  width: "100%",
};

export const platformCardSx = {
  backgroundColor: "var(--platform-surface)",
  borderRadius: platformRadius.lg,
  border: "1px solid var(--platform-border)",
  boxShadow: platformShadow.card,
  color: "var(--platform-text)",
};

export const platformPageTitleSx = {
  fontFamily: platformFonts.display,
  fontWeight: 700,
  fontSize: { xs: "1.125rem", sm: "1.3125rem" },
  color: "var(--platform-text)",
  letterSpacing: "-0.02em",
  lineHeight: 1.2,
};

export const platformPageSubtitleSx = {
  fontFamily: platformFonts.body,
  fontSize: "0.8125rem",
  color: "var(--platform-text-3)",
  mt: 0.5,
};

/** HTML `.btn` / `.btn-primary` */
export const platformPrimaryButtonSx = {
  backgroundColor: "var(--platform-brand)",
  color: "#fff",
  fontFamily: platformFonts.body,
  fontWeight: 600,
  fontSize: "13.5px",
  textTransform: "none",
  borderRadius: "10px",
  px: 2.25,
  py: 1.125,
  boxShadow: "none",
  lineHeight: 1.2,
  transition: "all 0.18s",
  "&:hover": {
    backgroundColor: "var(--platform-brand-light)",
    boxShadow: "none",
  },
  "&:focus-visible": {
    outline: "2px solid var(--platform-brand-light)",
    outlineOffset: 2,
  },
};

/** HTML `.btn-ghost` */
export const platformGhostButtonSx = {
  color: "var(--platform-text-2)",
  borderColor: "var(--platform-border-2)",
  borderWidth: 1.5,
  fontFamily: platformFonts.body,
  fontWeight: 600,
  fontSize: "13.5px",
  textTransform: "none",
  borderRadius: "10px",
  px: 2.25,
  py: 1.125,
  bgcolor: "transparent",
  transition: "all 0.18s",
  "&:hover": {
    backgroundColor: "var(--platform-brand-pale)",
    borderColor: "var(--platform-brand)",
    color: "var(--platform-brand)",
  },
  "&:focus-visible": {
    outline: "2px solid var(--platform-brand)",
    outlineOffset: 2,
  },
};

/** HTML `.btn-danger` */
export const platformDangerButtonSx = {
  backgroundColor: "var(--platform-danger-surface)",
  color: "var(--platform-danger)",
  fontFamily: platformFonts.body,
  fontWeight: 600,
  fontSize: "13.5px",
  textTransform: "none",
  borderRadius: "10px",
  boxShadow: "none",
  border: "none",
  transition: "all 0.18s",
  "&:hover": {
    backgroundColor: "var(--platform-danger)",
    color: "#fff",
  },
  "&:focus-visible": {
    outline: "2px solid var(--platform-danger)",
    outlineOffset: 2,
  },
};

/** HTML `.btn-sm` */
export const platformButtonSmallSx = {
  py: 0.75,
  px: 1.5,
  fontSize: "12px",
  borderRadius: "8px",
};

export const platformSectionTitleSx = {
  fontFamily: platformFonts.display,
  fontWeight: 700,
  fontSize: "15px",
  color: "var(--platform-text)",
  lineHeight: 1.3,
};

export const platformProgressTrackSx = {
  height: 6,
  bgcolor: "var(--platform-surface-3)",
  borderRadius: 99,
  overflow: "hidden",
};

export const platformScannerPanelSx = {
  bgcolor: "var(--platform-shell-bg)",
  borderRadius: platformRadius.xl,
  p: 3.5,
  textAlign: "center",
  border: "1px solid rgba(107, 45, 197, 0.3)",
};

export const platformStatLabelSx = {
  fontFamily: platformFonts.body,
  fontSize: "0.6875rem",
  fontWeight: 600,
  color: "var(--platform-text-3)",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};

export const platformStatValueSx = {
  fontFamily: platformFonts.display,
  fontWeight: 700,
  fontSize: { xs: "1.5rem", sm: "1.75rem" },
  color: "var(--platform-text)",
  lineHeight: 1,
};

/** HTML `.stat-card` padding */
export const platformStatCardSx = {
  ...platformCardSx,
  p: 2.5,
};

export function getPlatformBadgeVariants(mode = "light") {
  if (mode === "dark") {
    return {
      green: { bg: "rgba(16, 185, 129, 0.2)", color: "#6EE7B7" },
      red: { bg: "rgba(230, 59, 59, 0.2)", color: "#FCA5A5" },
      amber: { bg: "rgba(245, 158, 11, 0.2)", color: "#FCD34D" },
      blue: { bg: "rgba(59, 130, 246, 0.2)", color: "#93C5FD" },
      purple: { bg: "var(--platform-brand-pale)", color: "var(--platform-brand-light)" },
      gray: { bg: "rgba(255, 255, 255, 0.08)", color: "var(--platform-text-2)" },
      pink: { bg: "rgba(236, 72, 153, 0.2)", color: "#F9A8D4" },
    };
  }
  return {
    green: { bg: "#D1FAE5", color: "#065F46" },
    red: { bg: "#FEE2E2", color: "#991B1B" },
    amber: { bg: "#FEF3C7", color: "#92400E" },
    blue: { bg: "#DBEAFE", color: "#1E40AF" },
    purple: { bg: "var(--platform-brand-pale)", color: "var(--platform-brand)" },
    gray: { bg: "#F3F4F6", color: "#4B5563" },
    pink: { bg: "#FCE7F3", color: "#9D174D" },
  };
}

/** @deprecated Prefer getPlatformBadgeVariants(mode) */
export const platformBadgeVariants = getPlatformBadgeVariants("light");

/** Nested section inside platform forms (create event, etc.) */
export const platformFormSectionSx = {
  p: 2,
  borderRadius: platformRadius.md,
  background: "var(--platform-surface-2)",
  border: "1px solid var(--platform-border)",
};

/** Modal / dialog paper shell */
export const platformModalPaperSx = {
  backgroundColor: "var(--platform-surface)",
  color: "var(--platform-text)",
  border: "1px solid var(--platform-border)",
  borderRadius: platformRadius.lg,
  boxShadow: platformShadow.card,
};

/** Platform form fields — theme-aware (CSS variables) */
export const platformFormFieldSx = {
  "& .MuiInputLabel-root": {
    fontFamily: platformFonts.body,
    fontWeight: 600,
    fontSize: "13px",
    color: "var(--platform-text-2)",
    "&.Mui-focused": { color: "var(--platform-brand)" },
  },
  "& .MuiInputBase-input": {
    fontFamily: platformFonts.body,
    fontSize: "14px",
    color: "var(--platform-text)",
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "var(--platform-surface-2)",
    fontFamily: platformFonts.body,
    "& fieldset": {
      border: "1.5px solid var(--platform-border-2)",
    },
    "&:hover fieldset": { borderColor: "var(--platform-brand)" },
    "&.Mui-focused fieldset": {
      borderColor: "var(--platform-brand)",
      borderWidth: "1.5px",
    },
  },
};

/** White filter / form panel on platform pages */
export const platformFilterPaperSx = {
  ...platformCardSx,
  p: { xs: 2, sm: 2.5 },
  mb: 2.5,
};

export const platformFilterSelectSx = {
  borderRadius: platformRadius.sm,
  fontFamily: platformFonts.body,
  fontSize: "0.8125rem",
  color: "var(--platform-text)",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "var(--platform-border-2)",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "var(--platform-border-2)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "var(--platform-brand)",
  },
  "& .MuiSelect-select": {
    color: "var(--platform-text)",
  },
};

export const platformTableSx = {
  "& .MuiTableCell-root": {
    fontFamily: platformFonts.body,
    fontSize: "0.8125rem",
    color: "var(--platform-text-2)",
    borderColor: "var(--platform-border)",
    py: 1.25,
  },
  "& .MuiTableHead-root .MuiTableCell-root": {
    fontFamily: platformFonts.display,
    fontWeight: 700,
    fontSize: "0.65rem",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: "var(--platform-text-3)",
    bgcolor: "var(--platform-surface-2)",
  },
  "& .MuiTableRow-root:hover .MuiTableCell-root": {
    bgcolor: "var(--platform-surface-2)",
  },
};

/** HTML `.search-input` */
export const platformSearchInputSx = {
  fontFamily: platformFonts.body,
  fontSize: "13.5px",
  color: "var(--platform-text)",
  backgroundColor: "var(--platform-surface)",
  borderRadius: "10px",
  "& .MuiOutlinedInput-input": {
    py: 1.125,
    px: 1.5,
    pl: 0.5,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "var(--platform-border-2)",
    borderWidth: 1.5,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "var(--platform-border-2)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "var(--platform-brand)",
    borderWidth: 1.5,
  },
};
